import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, X, List, Hash, GripVertical } from 'lucide-react'
import { adminQuestionnairesService } from '../services'
import './PlanQuiz.css'

// Map UI question { text, type, choices } to API shape { title, questionType, options }
const toApiQuestions = (questions) =>
    questions.map((q) => ({
        title: q.text || 'Question',
        questionType: q.type === 'multi' ? 'multi' : 'single',
        options: Array.isArray(q.choices) ? q.choices.filter((c) => c != null && String(c).trim() !== '') : [],
    }))

// Map API question to UI shape
const fromApiQuestion = (q, index) => ({
    id: q.id || `q-${index}`,
    text: q.title || '',
    type: q.questionType === 'multi' ? 'multi' : 'single',
    required: true,
    choices: Array.isArray(q.options) ? [...q.options] : [],
})

const PlanQuiz = () => {
    const [questionnaireId, setQuestionnaireId] = useState(null)
    const [questions, setQuestions] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    const [isEditing, setIsEditing] = useState(false)
    const [currentQuestion, setCurrentQuestion] = useState(null)

    // Drag state for reordering questions
    const [draggingQuestionIndex, setDraggingQuestionIndex] = useState(null)

    const loadQuestionnaire = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const items = await adminQuestionnairesService.list()
            if (items && items.length > 0) {
                const first = items[0]
                setQuestionnaireId(first.id)
                const full = await adminQuestionnairesService.getById(first.id)
                const qs = (full.questions || []).map((q, i) => fromApiQuestion(q, i))
                setQuestions(qs)
            } else {
                setQuestionnaireId(null)
                setQuestions([])
            }
        } catch (err) {
            console.error('Load questionnaire failed:', err)
            setError(err.response?.data?.error || err.message || 'Failed to load questionnaire')
            setQuestions([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadQuestionnaire()
    }, [loadQuestionnaire])

    const saveToBackend = async (questionList) => {
        const payload = { title: 'Create your plan', questions: toApiQuestions(questionList) }
        if (questionnaireId) {
            await adminQuestionnairesService.update(questionnaireId, payload)
        } else {
            const created = await adminQuestionnairesService.create(payload)
            setQuestionnaireId(created.id)
        }
    }

    const handleAdd = () => {
        setCurrentQuestion({
            id: `new-${Date.now()}`,
            text: '',
            type: 'single',
            required: true,
            choices: ['', ''],
        })
        setIsEditing(true)
    }

    const handleEdit = (question) => {
        setCurrentQuestion({ ...question })
        setIsEditing(true)
    }

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return
        const next = questions.filter((q) => q.id !== id)
        setQuestions(next)
        setError(null)
        try {
            setSaving(true)
            await saveToBackend(next)
        } catch (err) {
            console.error('Delete/save failed:', err)
            setError(err.response?.data?.error || err.message || 'Failed to save')
            setQuestions(questions)
        } finally {
            setSaving(false)
        }
    }

    const handleSave = async (e) => {
        e.preventDefault()
        let nextQuestions
        if (questions.find((q) => q.id === currentQuestion.id)) {
            nextQuestions = questions.map((q) => (q.id === currentQuestion.id ? currentQuestion : q))
        } else {
            nextQuestions = [...questions, currentQuestion]
        }
        setQuestions(nextQuestions)
        setIsEditing(false)
        setCurrentQuestion(null)
        setError(null)
        try {
            setSaving(true)
            await saveToBackend(nextQuestions)
        } catch (err) {
            console.error('Save questionnaire failed:', err)
            setError(err.response?.data?.error || err.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const handleChoiceChange = (index, value) => {
        const newChoices = [...currentQuestion.choices]
        newChoices[index] = value
        setCurrentQuestion({ ...currentQuestion, choices: newChoices })
    }

    const handleAddChoice = () => {
        setCurrentQuestion({
            ...currentQuestion,
            choices: [...currentQuestion.choices, '']
        })
    }

    const handleRemoveChoice = (index) => {
        if (currentQuestion.choices.length <= 1) {
            alert('A question must have at least one choice.')
            return
        }
        const newChoices = currentQuestion.choices.filter((_, i) => i !== index)
        setCurrentQuestion({ ...currentQuestion, choices: newChoices })
    }

    // Drag-and-drop for question ordering
    const handleQuestionDragStart = (index) => {
        setDraggingQuestionIndex(index)
    }

    const handleQuestionDragOver = (event) => {
        event.preventDefault()
    }

    const handleQuestionDrop = async (targetIndex) => {
        if (draggingQuestionIndex === null || draggingQuestionIndex === targetIndex) return
        const reordered = [...questions]
        const [moved] = reordered.splice(draggingQuestionIndex, 1)
        reordered.splice(targetIndex, 0, moved)
        setDraggingQuestionIndex(null)
        setQuestions(reordered)
        setError(null)
        try {
            setSaving(true)
            await saveToBackend(reordered)
        } catch (err) {
            console.error('Reorder/save failed:', err)
            setError(err.response?.data?.error || err.message || 'Failed to save new order')
        } finally {
            setSaving(false)
        }
    }

    // Drag-and-drop for choices inside the edit form
    const handleChoiceDragStart = (index) => {
        setCurrentQuestion((prev) =>
            prev
                ? {
                      ...prev,
                      _draggingChoiceIndex: index,
                  }
                : prev
        )
    }

    const handleChoiceDragOver = (event) => {
        event.preventDefault()
    }

    const handleChoiceDrop = (targetIndex) => {
        if (
            !currentQuestion ||
            currentQuestion._draggingChoiceIndex === undefined ||
            currentQuestion._draggingChoiceIndex === targetIndex
        ) {
            return
        }
        const from = currentQuestion._draggingChoiceIndex
        const newChoices = [...currentQuestion.choices]
        const [moved] = newChoices.splice(from, 1)
        newChoices.splice(targetIndex, 0, moved)
        const { _draggingChoiceIndex, ...rest } = currentQuestion
        setCurrentQuestion({
            ...rest,
            choices: newChoices,
        })
    }

    if (loading) {
        return (
            <div className="plan-quiz-page">
                <div className="page-header">
                    <div className="header-titles">
                        <h1>Plan Questionnaire Management</h1>
                        <div className="loading-state">
                            <div className="loading-spinner" />
                            <span>Loading questionnaire…</span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="plan-quiz-page">
            <div className="page-header">
                <div className="header-titles">
                    <h1>Plan Questionnaire Management</h1>
                    <p>Manage the questions used to create the best plan for the user.</p>
                </div>
            </div>

            {error && (
                <div style={{ padding: '1rem', marginBottom: '1rem', background: '#fee', color: '#c00', borderRadius: 8 }}>
                    {error}
                </div>
            )}
            {saving && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', color: '#666' }}>Saving…</div>
            )}

            <div className="quiz-header-actions" style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
                <button className="add-btn" onClick={handleAdd} disabled={saving}>
                    <Plus size={20} /> Add Question
                </button>
            </div>

            {isEditing ? (
                <div className="question-card">
                    <form className="edit-form" onSubmit={handleSave}>
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Question Text</label>
                                <input
                                    className="form-input"
                                    value={currentQuestion.text}
                                    onChange={(e) =>
                                        setCurrentQuestion({ ...currentQuestion, text: e.target.value })
                                    }
                                    required
                                    placeholder="Enter question text..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Selection Type</label>
                                <select
                                    className="form-input"
                                    value={currentQuestion.type}
                                    onChange={(e) =>
                                        setCurrentQuestion({ ...currentQuestion, type: e.target.value })
                                    }
                                >
                                    <option value="single">Single Select</option>
                                    <option value="multi">Multi Select</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <label style={{ margin: 0 }}>Choices</label>
                                <button
                                    type="button"
                                    className="add-choice-btn"
                                    onClick={handleAddChoice}
                                >
                                    <Plus size={16} /> Add Choice
                                </button>
                            </div>
                            <div className="choices-edit-grid">
                                {currentQuestion.choices.map((choice, index) => (
                                    <div key={index} className="choice-edit-row">
                                        <span
                                            title="Drag to reorder choice"
                                            style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#888' }}
                                            draggable
                                            onDragStart={() => handleChoiceDragStart(index)}
                                            onDragOver={handleChoiceDragOver}
                                            onDrop={() => handleChoiceDrop(index)}
                                        >
                                            <GripVertical size={14} />
                                        </span>
                                        <input
                                            className="form-input"
                                            style={{ flex: 1 }}
                                            value={choice}
                                            onChange={(e) => handleChoiceChange(index, e.target.value)}
                                            required
                                            placeholder={`Choice ${index + 1}`}
                                        />
                                        <button
                                            type="button"
                                            className="action-icon-btn delete"
                                            onClick={() => handleRemoveChoice(index)}
                                            title="Remove Choice"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="save-btn">
                                Save Question
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="questions-list">
                    {questions.map((q, index) => (
                        <div
                            key={q.id}
                            className="question-card"
                            draggable
                            onDragStart={() => handleQuestionDragStart(index)}
                            onDragOver={handleQuestionDragOver}
                            onDrop={() => handleQuestionDrop(index)}
                        >
                            <div className="question-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <span
                                        title="Drag to reorder"
                                        style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#888' }}
                                    >
                                        <GripVertical size={18} />
                                    </span>
                                    <span className="question-title">
                                        {index + 1}. {q.text}
                                    </span>
                                    <span className={`type-badge ${q.type}`}>
                                        {q.type === 'multi' ? <List size={12} /> : <Hash size={12} />}
                                        {q.type === 'multi' ? 'Multi Select' : 'Single Select'}
                                    </span>
                                </div>
                                <div className="question-actions">
                                    <button
                                        className="action-icon-btn edit"
                                        onClick={() => handleEdit(q)}
                                    >
                                        <Pencil size={18} />
                                    </button>
                                    <button
                                        className="action-icon-btn delete"
                                        onClick={() => handleDelete(q.id)}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                            <div className="choices-list">
                                {q.choices.map((choice, cIndex) => (
                                    <div
                                        key={cIndex}
                                        className="choice-box"
                                    >
                                        {choice}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default PlanQuiz
